import React, { useState } from "react";
import { useDispatch, useSelector } from 'react-redux'
import {
  DataTable, Table, TableBody, TableCell, TableContainer,
  TableHead, TableHeader, TableRow, TableToolbar, TableToolbarContent,
  TableToolbarSearch, Button, Pagination
} from '@carbon/react';
import { View, Download } from '@carbon/icons-react';
import { useSorting } from "../../../modules/hooks/sorting";
import { onGetTruckGpsPointsThunk } from "./CycleAnomaly.actions";

const CycleAnomalyTable = () => {
  const dispatch = useDispatch();
  const { data } = useSelector(state => state.CycleAnomaly);
  const { gpsPoints } = useSelector(state => state.CycleAnomaly);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const headerData = [
    {
      header: 'Patente',
      key: 'patente',
    },
    {
      header: 'Duración (min)',
      key: 'duracion',
    },
    {
      header: 'Fecha/Hora Inicio',
      key: 'inicio_ciclo',
      sortDirection: "DESC",
      isDefaultSortable: true
    },
    {
      header: 'Fecha/Hora Término',
      key: 'fin_ciclo',
      sortDirection: "DESC",
    },
    {
      header: 'Acción',
      key: 'accion',
    }
  ];

  const { rows, headers } = useSorting(data, headerData);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    // If it's already formatted (contains hyphen and colon)
    if (typeof timestamp === 'string' && timestamp.includes('-') && timestamp.includes(':')) {
      return timestamp;
    }

    // If it's a number or numeric string, convert to formatted date
    const date = new Date(Number(timestamp));

    // Format as YYYY-MM-DD HH:MM:SS
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const convertToTimestamp = (dateValue) => {
    // If it's already a timestamp (number), return as string
    if (typeof dateValue === 'number') {
      return String(dateValue);
    }

    // If it's a string that looks like a timestamp
    if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
      return dateValue;
    }

    // If it's a formatted date string, convert to timestamp
    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      return String(date.getTime());
    }

    return String(dateValue);
  };

  const convertToCSV = (data) => {
    const headerMapping = {
      patente: 'Patente',
      duracion: 'Duración (min)',
      inicio_ciclo: 'Fecha/Hora Inicio',
      fin_ciclo: 'Fecha/Hora Término'
    };

    if (data.length === 0) {
      return '';
    }

    const headers = Object.values(headerMapping).join(',') + '\n';
    const rows = data.map(row => {
      return Object.keys(headerMapping).map(key => {
        let value = row[key];

        // Handle null or undefined values
        if (value === null || value === undefined) {
          value = '';
        } else if (key === 'inicio_ciclo') {
          // Format inicio_ciclo timestamp
          value = formatTimestamp(value);
        } else if (!isNaN(value) && typeof value === 'number' && value.toString().includes('.')) {
          // If it's a number with a dot, replace the dot with a comma
          value = value.toString().replace('.', ',');
        }

        return `"${value}"`; // Apply text formatting to all fields
      }).join(',');
    }).join('\n');

    return headers + rows;
  };

  const downloadCSV = (csvContent, fileName = 'download.csv') => {
    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create a hidden link element
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    // Append link to the body, click it, and then remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const csv = convertToCSV(data);
    downloadCSV(csv, 'Ciclos_Anomalos.csv');
  };

  const handleViewGpsPoints = (row) => {
    const rowData = data.find(item => item.patente === row.cells[0].value);

    // Convert both dates to timestamp format
    const fechaInicio = convertToTimestamp(rowData.inicio_ciclo);
    const fechaTermino = convertToTimestamp(rowData.fin_ciclo);

    dispatch(onGetTruckGpsPointsThunk({
      patente: rowData.patente,
      fecha_inicio: fechaInicio,
      fecha_termino: fechaTermino
    }));
  };

  const Default = () => (
    <DataTable rows={rows} headers={headers} width="100%">
      {({
        rows,
        headers,
        getHeaderProps,
        getRowProps,
        getTableProps,
        getTableContainerProps,
        onInputChange,
        sortBy,
      }) => (
        <TableContainer {...getTableContainerProps()}>
          <TableToolbar>
            <TableToolbarContent>
              <TableToolbarSearch onChange={onInputChange} />
              <Button onClick={handleDownload} renderIcon={Download}>
                Descargar
              </Button>
            </TableToolbarContent>
          </TableToolbar>
          <Table {...getTableProps()} aria-label="cycle anomaly table">
            <TableHead>
              <TableRow>
                {headers.map((header, i) => {
                  const { key: _unusedKey, ...headerProps } = getHeaderProps({
                    header,
                    isSortable:
                      (headerData[i]?.sortDirection ?? "NONE") !== "NONE",
                    onClick: (_, sortState) => sortBy(sortState.sortHeaderKey),
                  });

                  return (
                    <TableHeader
                      key={i}
                      sortDirection={headerData[i]?.sortDirection ?? "NONE"}
                      {...headerProps}
                    >
                      {header.header}
                    </TableHeader>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice((page - 1) * pageSize, page * pageSize).map((row) => {
                const { key: _unusedKey, ...rowProps } = getRowProps({ row });

                return (
                  <React.Fragment key={row.id}>
                    <TableRow {...rowProps}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>
                          {cell.info.header === 'accion' ? (
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={View}
                              iconDescription="Ver puntos GPS"
                              onClick={() => handleViewGpsPoints(row)}
                              disabled={gpsPoints.loading}
                            >
                              Cargar al mapa
                            </Button>
                          ) : cell.info.header === 'inicio_ciclo' ? (
                            formatTimestamp(cell.value)
                          ) : (
                            cell.value
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          <Pagination
            totalItems={rows.length}
            pageSizes={[10, 15, 20]}
            itemsPerPageText="Elementos por página"
            pageRangeText={(current, total) => `${current}–${total} página(s)`}
            itemRangeText={(min, max, total) => `${min}–${max} de ${total} elementos`}
            pageNumberText="Número de página"
            page={page}
            pageSize={pageSize}
            onChange={({ page, pageSize }) => {
              setPage(page);
              setPageSize(pageSize);
            }}
            forwardText="Página siguiente"
            backwardText="Página anterior"
          />
        </TableContainer>
      )}
    </DataTable>
  );

  return <Default />
}

export default CycleAnomalyTable;